import { useEffect, useRef, useState } from "react";

export type Box = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * A placed image: the cell it occupies in the grid, and the rectangle the image
 * is actually drawn in.
 *
 * For an ordinary photo the two are identical. For a ratio outside the packing
 * band — a 1:20 screenshot, a 20:1 panorama — the cell is clamped so the row
 * math stays sane while `draw` keeps the true shape, centred. That is the
 * difference between a mounted print and a crop: the whole image is always
 * visible, and the empty space reads as a mat rather than as damage.
 */
export type Placed = {
  box: Box;
  draw: Box;
  /** True when the image does not fill its cell and needs a mat around it. */
  matted: boolean;
};

/**
 * Ratios outside this band are still shown whole, but they get a row to
 * themselves, spanning the full width at a bounded height. Without a bound, a
 * 1:10 screenshot would produce a tile ten times the width of the gallery, and
 * sharing a row with a 4:5 portrait would leave it a few pixels wide.
 */
export const MIN_RATIO = 0.34;
export const MAX_RATIO = 3.2;

/**
 * A row is never scaled beyond these multiples of the target height. The upper
 * bound is what keeps a tall portrait from dominating the page; the lower one
 * stops a wide image being blown up out of proportion.
 */
export const MIN_ROW_SCALE = 0.55;
export const MAX_ROW_SCALE = 1.9;

/**
 * Justified rows: images keep their true aspect ratio, and each row is scaled
 * to exactly fill the container width.
 *
 * This is why the gallery never looks like a fixed grid. A 4:5 portrait, a
 * 3:2 landscape and a square all sit at their natural shape, and the row grows
 * or shrinks to accommodate them, so there is no cropping and no letterboxing
 * anywhere. Every row, including the last, is scaled to fill the width within
 * fixed height bounds, so a short final row cannot leave a ragged edge and a
 * wide screen cannot blow two small images up into obvious blur.
 */
export function justify<T>(
  items: readonly T[],
  ratioOf: (item: T) => number,
  containerWidth: number,
  targetHeight: number,
  gap: number,
): { placed: Placed[]; height: number } {
  const placed: Placed[] = [];
  if (items.length === 0 || containerWidth <= 0) return { placed, height: 0 };

  const minH = targetHeight * MIN_ROW_SCALE;
  const maxH = targetHeight * MAX_ROW_SCALE;
  const safeRatio = (value: number, clamp = true) => {
    if (!Number.isFinite(value) || value <= 0) return 1;
    return clamp ? Math.min(Math.max(value, MIN_RATIO), MAX_RATIO) : value;
  };
  /** True for a shape too extreme to sit comfortably in a mixed row. */
  const outOfBand = (value: number) =>
    !Number.isFinite(value) || value <= 0 || value < MIN_RATIO || value > MAX_RATIO;

  let y = 0;
  let row: T[] = [];
  let rowRatio = 0;

  const flush = (stretch: boolean) => {
    if (row.length === 0) return;
    const gaps = gap * (row.length - 1);
    const available = Math.max(containerWidth - gaps, 1);

    // A panorama or a tall screenshot is given the whole row to itself and
    // spans the full width, the way a wide photograph is meant to be hung.
    // Sharing a row with ordinary work would squeeze it into a sliver next to
    // them, which reads as a rendering fault rather than a deliberate choice.
    const banner = row.length === 1 && outOfBand(ratioOf(row[0]!));

    let height = targetHeight;
    if (banner) {
      // Full width, height from the image's own ratio, bounded like any row.
      height = Math.min(Math.max(available / safeRatio(ratioOf(row[0]!), false), minH), maxH);
    } else if (stretch) {
      // Scale the row so the tiles plus gaps exactly fill the width.
      height = available / rowRatio;
      height = Math.min(Math.max(height, minH), maxH);
    }

    let x = 0;
    row.forEach((item, i) => {
      const cellH = Math.round(height);
      const cellW = banner
        ? available
        : Math.max(1, Math.round(safeRatio(ratioOf(item)) * height));

      // Fit the true ratio inside the cell rather than the other way round, so
      // clamping the cell never distorts or crops the picture.
      const trueRatio = safeRatio(ratioOf(item), false);
      let drawW = cellW;
      let drawH = cellW / trueRatio;
      if (drawH > cellH) {
        drawH = cellH;
        drawW = cellH * trueRatio;
      }
      drawW = Math.max(1, Math.round(drawW));
      drawH = Math.max(1, Math.round(drawH));

      placed.push({
        box: { x, y, width: cellW, height: cellH },
        draw: {
          x: x + Math.round((cellW - drawW) / 2),
          y: y + Math.round((cellH - drawH) / 2),
          width: drawW,
          height: drawH,
        },
        matted: Math.abs(drawW - cellW) > 1 || Math.abs(drawH - cellH) > 1,
      });
      // No gap after the last tile: a trailing one would push the row a gap
      // wider than the width it was just fitted to.
      if (i < row.length - 1) x += cellW + gap;
    });

    y += Math.round(height) + gap;
    row = [];
    rowRatio = 0;
  };

  for (const item of items) {
    const banded = outOfBand(ratioOf(item));
    // Flush whatever is already here so the extreme image gets a clean row.
    if (banded && row.length > 0) flush(true);

    row.push(item);
    rowRatio += safeRatio(ratioOf(item));

    const projected = rowRatio * targetHeight + gap * (row.length - 1);
    if (banded || projected > containerWidth) {
      // Always fitted to the width, including when the row holds a single
      // image — otherwise an unusually wide or tall picture overflows.
      flush(true);
    }
  }
  // The last row is fitted to the width as well, so a gallery that happens to
  // end on a short row does not leave a ragged gap along the right edge. The
  // row-height clamps are what stop a two-image last row on a wide screen being
  // blown up into enormous tiles, so stretching here is bounded, not reckless.
  flush(true);

  return { placed, height: Math.max(0, y - gap) };
}

/**
 * Container width, tracked live.
 *
 * Returns 0 until the first measurement so the first paint is never laid out
 * against a guessed width and then visibly reflowed.
 */
export function useContainerWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver(entries => {
      const next = entries[0]?.contentRect.width ?? 0;
      // Rounding avoids a re-render for sub-pixel resize noise.
      setWidth(previous => (Math.abs(previous - next) < 1 ? previous : next));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
}

/**
 * Row height for the current container.
 *
 * Derived from the width rather than hard-coded per breakpoint, so the gallery
 * keeps roughly the same number of pieces per row on a phone as on a desktop
 * and the layout stays sane in a resized browser window or an embedded frame.
 */
export function targetRowHeight(containerWidth: number) {
  if (containerWidth <= 0) return 200;
  if (containerWidth < 480) return 132;
  if (containerWidth < 768) return 176;
  if (containerWidth < 1280) return 210;
  return 236;
}

export function galleryGap(containerWidth: number) {
  return containerWidth < 768 ? 8 : 12;
}
